import Head from 'next/head';
import { NextSeo } from 'next-seo';
import type { ReactElement } from 'react';
import {
    buildBusinessGraph,
    buildWebPageSchema,
    getImageType,
    normaliseSeoMeta,
    stringifyJsonLd,
    toAbsoluteUrl,
} from './schema';
import type { JsonLdObject, JsonLdScriptItem, PageSeoProps, SeoMetaProps, SiteSeoProfile } from './types';

export function JsonLdScript({ id, schema }: JsonLdScriptItem) {
    if (!schema) return null;

    return (
        <Head>
            <script
                id={id}
                key={id}
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: stringifyJsonLd(schema) }}
            />
        </Head>
    );
}

export function JsonLdScripts({ items }: { items: JsonLdScriptItem[] }) {
    return (
        <>
            {items.map((item) => (
                <JsonLdScript key={item.id} {...item} />
            ))}
        </>
    );
}

export function BusinessJsonLd({ siteProfile, id = 'local-business-json-ld' }: { siteProfile: SiteSeoProfile; id?: string }) {
    return <JsonLdScript id={id} schema={buildBusinessGraph(siteProfile)} />;
}

export function MetaTags({
    meta,
    siteProfile,
    includeWebPageSchema = true,
    imageWidth = 1200,
    imageHeight = 630,
}: SeoMetaProps) {
    const pageMeta = normaliseSeoMeta(meta, siteProfile);
    const canonicalUrl = toAbsoluteUrl(pageMeta.url, siteProfile.siteUrl) || siteProfile.siteUrl;
    const image = toAbsoluteUrl(pageMeta.ogImage || siteProfile.defaultImage, siteProfile.siteUrl) || '';
    const imageAlt = pageMeta.ogImageAlt || pageMeta.title;
    const imageType = getImageType(image);
    const robots = [pageMeta.noindex ? 'noindex' : undefined, pageMeta.nofollow ? 'nofollow' : undefined]
        .filter(Boolean)
        .join(',');

    return (
        <>
            <NextSeo
                title={pageMeta.title}
                description={pageMeta.description}
                canonical={canonicalUrl}
                noindex={pageMeta.noindex}
                nofollow={pageMeta.nofollow}
                openGraph={{
                    type: pageMeta.type || 'website',
                    url: canonicalUrl,
                    title: pageMeta.title,
                    description: pageMeta.description,
                    images: [
                        {
                            url: image,
                            secureUrl: image,
                            type: imageType,
                            width: imageWidth,
                            height: imageHeight,
                            alt: imageAlt,
                        },
                    ],
                }}
                twitter={{
                    cardType: pageMeta.twitterCardType || 'summary_large_image',
                }}
            />
            <Head>
                {pageMeta.keywords ? <meta name="keywords" content={pageMeta.keywords} key="keywords" /> : null}
                {robots ? <meta name="robots" content={robots} key="robots" /> : null}
                <meta charSet="UTF-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" key="x-ua-compatible" />
                <meta name="twitter:title" content={pageMeta.title} key="twitter:title" />
                <meta name="twitter:description" content={pageMeta.description} key="twitter:description" />
                <meta name="twitter:image" content={image} key="twitter:image" />
                <meta name="twitter:image:alt" content={imageAlt} key="twitter:image:alt" />
                <meta name="twitter:url" content={canonicalUrl} key="twitter:url" />
                {siteProfile.faviconHref ? <link rel="icon" href={siteProfile.faviconHref} key="favicon" /> : null}
                {includeWebPageSchema ? (
                    <script
                        key={`${canonicalUrl}-webpage-json-ld`}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: stringifyJsonLd(buildWebPageSchema(pageMeta, siteProfile)),
                        }}
                    />
                ) : null}
            </Head>
        </>
    );
}

export function PageSeo({
    meta,
    siteProfile,
    includeWebPageSchema = true,
    includeBusinessSchema = false,
    schemas = [],
    children,
}: PageSeoProps): ReactElement {
    return (
        <>
            {includeBusinessSchema ? <BusinessJsonLd siteProfile={siteProfile} /> : null}
            <MetaTags
                meta={meta}
                siteProfile={siteProfile}
                includeWebPageSchema={includeWebPageSchema}
            />
            <JsonLdScripts items={schemas} />
            {children}
        </>
    );
}

export type { JsonLdObject, JsonLdScriptItem, PageSeoProps, SeoMetaProps, SiteSeoProfile };
